import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, ApiError } from "@/lib/api/client";
import { PinEntryForm } from "@/components/shared/pin-entry-form";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { Heading } from "@/components/ui/heading";
import { Surface } from "@/components/ui/surface";

type TokenPurpose =
  | "DocAccess"
  | "DriverHandoff"
  | "InviteCarrier"
  | "PickupConfirm"
  | "DeliveryConfirm"
  | "TransferConfirm"
  | "DocBundle"
  | "Invitation"
  | "TaskReassign";

type TokenEntityType =
  | "Load"
  | "Trip"
  | "Truck"
  | "Driver"
  | "EntityApplyForm"
  | "LoadStop"
  | "TicketStop";

interface ScanResult {
  success: boolean;
  error?: string;
  token?: {
    id: string;
    purpose: TokenPurpose;
    entity_type: TokenEntityType;
    entity_id: string;
    requires_pin: boolean;
    actions: { id: string; status: string; payload: unknown }[];
  };
}

const PURPOSE_LABELS: Record<TokenPurpose, string> = {
  DocAccess: "Document Access",
  DriverHandoff: "Driver Handoff",
  InviteCarrier: "Carrier Invitation",
  PickupConfirm: "Pickup Confirmation",
  DeliveryConfirm: "Delivery Confirmation",
  TransferConfirm: "Transfer Confirmation",
  DocBundle: "Document Bundle",
  Invitation: "Invitation",
  TaskReassign: "Task Reassignment",
};

const ENTITY_LABELS: Record<TokenEntityType, string> = {
  Load: "Load",
  Trip: "Trip",
  Truck: "Truck",
  Driver: "Driver",
  EntityApplyForm: "Application",
  LoadStop: "Stop",
  TicketStop: "Ticket Stop",
};

function QrCodePage() {
  const { code: rawCode } = Route.useParams();
  const { t: tCommon } = useTranslation("common");
  const code = decodeURIComponent(rawCode);

  const [status, setStatus] = useState<
    "loading" | "scanned" | "pin" | "done" | "error" | "auth-required"
  >("loading");
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function scan() {
      try {
        const result = await apiClient.post<ScanResult>("/qr-tokens/scan", {
          code,
        });

        if (cancelled) return;

        if (!result.success) {
          setErrorMessage(result.error || "Invalid QR code");
          setStatus("error");
          return;
        }

        setScanResult(result);

        if (result.token?.requires_pin) {
          setStatus("pin");
        } else {
          setStatus("scanned");
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to scan QR code";
        setErrorMessage(message);
        setStatus("error");
      }
    }

    void scan();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleVerifyPin = async (pinValue: string) => {
    if (!scanResult?.token) return;

    await apiClient.post("/qr-tokens/verify-pin", {
      token_id: scanResult.token.id,
      pin: pinValue,
    });
    setStatus("scanned");
  };

  const handleConsume = async () => {
    if (!scanResult?.token) return;

    const pendingAction = scanResult.token.actions.find(
      (a) => a.status === "Pending",
    );
    if (!pendingAction) return;

    try {
      await apiClient.post("/qr-tokens/consume", {
        action_id: pendingAction.id,
      });
      setStatus("done");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setStatus("auth-required");
      } else {
        setErrorMessage(err instanceof Error ? err.message : "Action failed");
        setStatus("error");
      }
    }
  };

  // --- Loading ---
  if (status === "loading") {
    return (
      <Surface
        bg="container"
        rounded="2xl"
        className="p-8 text-center"
        data-testid="qr-page-loading"
      >
        <VStack gap="lg" className="animate-pulse">
          <Surface bg="high" className="h-4 rounded w-3/4 mx-auto" />
          <Surface bg="high" className="h-4 rounded w-1/2 mx-auto" />
        </VStack>
        <Text color="muted" className="mt-4">
          {tCommon("loading")}
        </Text>
      </Surface>
    );
  }

  // --- Error ---
  if (status === "error") {
    return (
      <Surface bg="container" rounded="2xl" className="p-8 text-center">
        <VStack gap="lg" data-testid="qr-page-error">
          <div className="text-4xl">&#10060;</div>
          <Heading size="md">Invalid QR Code</Heading>
          <Text color="muted">{errorMessage}</Text>
        </VStack>
      </Surface>
    );
  }

  // --- PIN entry ---
  if (status === "pin") {
    return (
      <Surface bg="container" rounded="2xl" className="p-8">
        <VStack gap="lg" data-testid="qr-page-pin">
          <Heading size="md" className="text-center">
            PIN Required
          </Heading>
          <Text color="muted" align="center">
            Enter the PIN to continue
          </Text>
          <PinEntryForm
            maxLength={4}
            onSubmit={handleVerifyPin}
            data-testid="qr-page-pin"
          />
        </VStack>
      </Surface>
    );
  }

  // --- Auth required ---
  if (status === "auth-required") {
    const returnUrl = `/qr/${encodeURIComponent(code)}`;
    return (
      <Surface bg="container" rounded="2xl" className="p-8 text-center">
        <VStack gap="lg" data-testid="qr-page-auth">
          <Heading size="md">Login Required</Heading>
          <Text color="muted">You need to log in to complete this action.</Text>
          <Button
            fullWidth
            onClick={() =>
              (window.location.href = `/login?redirect=${encodeURIComponent(returnUrl)}`)
            }
            data-testid="qr-page-login-button"
          >
            Log In
          </Button>
        </VStack>
      </Surface>
    );
  }

  // --- Done ---
  if (status === "done") {
    return (
      <Surface bg="container" rounded="2xl" className="p-8 text-center">
        <VStack gap="lg" data-testid="qr-page-done">
          <div className="text-4xl">&#9989;</div>
          <Heading size="md">Action Completed</Heading>
          <Text color="muted">
            The QR code action has been processed successfully.
          </Text>
        </VStack>
      </Surface>
    );
  }

  // --- Scanned (show token info + actions) ---
  const token = scanResult?.token;
  const hasPendingAction = token?.actions.some((a) => a.status === "Pending");

  return (
    <Surface bg="container" rounded="2xl" className="p-8">
      <VStack gap="lg" data-testid="qr-page-scanned">
        <Heading size="md" className="text-center">
          QR Code Scanned
        </Heading>

        {token && (
          <VStack>
            <HStack
              justify="between"
              gap="none"
              className="py-2 border-b border-[color:var(--outline-variant)]"
            >
              <Text color="muted">Purpose</Text>
              <Text weight="medium">
                {PURPOSE_LABELS[token.purpose] || token.purpose}
              </Text>
            </HStack>
            <HStack
              justify="between"
              gap="none"
              className="py-2 border-b border-[color:var(--outline-variant)]"
            >
              <Text color="muted">Related To</Text>
              <Text weight="medium">
                {ENTITY_LABELS[token.entity_type] || token.entity_type}
              </Text>
            </HStack>
          </VStack>
        )}

        {hasPendingAction && (
          <Button
            fullWidth
            onClick={handleConsume}
            data-testid="qr-page-consume-button"
          >
            {tCommon("confirm")}
          </Button>
        )}

        {!hasPendingAction && (
          <Text color="muted" align="center">
            No pending actions for this QR code.
          </Text>
        )}
      </VStack>
    </Surface>
  );
}

export const Route = createFileRoute("/qr/$code")({
  component: QrCodePage,
});
